import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { api } from '../App';
import { History, CheckCircle2, Zap, AlertTriangle, TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';

const HEALTH_LABEL = {
  healthy: { cls: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2, text: 'Healthy' },
  slow: { cls: 'bg-amber-100 text-amber-800', Icon: Zap, text: 'Slow' },
  degraded: { cls: 'bg-rose-100 text-rose-800', Icon: AlertTriangle, text: 'Degraded' },
};

const fmtINR = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  return sign + '₹' + Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}, ${hh}:${mm}`;
};

const fmtDay = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d.getDate()}${months[d.getMonth()]}${d.getFullYear()}`;
};

const RefreshHistoryModal = ({ open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('events'); // 'events' | 'daily'

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/prices/refresh-history?limit=100');
      setData(res.data);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) fetchData(); }, [open, fetchData]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="refresh-history-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History size={18} /> Price Refresh History
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500 mt-1">
            Every refresh is recorded. Use this to track daily trends and diagnose flaky data sources.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-3">
          <div className="flex gap-1 text-xs">
            <button
              onClick={() => setView('events')}
              className={`px-3 py-1.5 rounded font-medium ${view === 'events' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              data-testid="view-events-btn"
            >Every Refresh</button>
            <button
              onClick={() => setView('daily')}
              className={`px-3 py-1.5 rounded font-medium ${view === 'daily' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              data-testid="view-daily-btn"
            >Daily Trend</button>
          </div>
          <button
            onClick={fetchData}
            className="ml-auto p-1.5 hover:bg-gray-100 rounded text-gray-600"
            title="Reload"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          {loading && <p className="text-sm text-gray-500 py-6 text-center">Loading…</p>}

          {!loading && view === 'events' && (
            data?.events?.length ? (
              <table className="w-full text-sm" data-testid="events-table">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">When</th>
                    <th className="text-right px-3 py-2 font-semibold">Portfolio After</th>
                    <th className="text-right px-3 py-2 font-semibold">Change</th>
                    <th className="text-right px-3 py-2 font-semibold">Stocks</th>
                    <th className="text-right px-3 py-2 font-semibold">MFs</th>
                    <th className="text-center px-3 py-2 font-semibold">Source</th>
                    <th className="text-right px-3 py-2 font-semibold">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {data.events.map(ev => {
                    const health = HEALTH_LABEL[ev.data_source_health || 'healthy'] || HEALTH_LABEL.healthy;
                    const Icon = health.Icon;
                    const change = ev.value_change || 0;
                    return (
                      <tr key={ev.id} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-700">{fmtDateTime(ev.created_at)}</td>
                        <td className="px-3 py-2 text-right font-medium">{fmtINR(ev.portfolio_value_after)}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${change >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                          <span className="inline-flex items-center gap-1">
                            {change >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                            {change >= 0 ? '+' : ''}{fmtINR(change)}
                            <span className="text-[10px] opacity-70 ml-1">({ev.change_percent?.toFixed?.(2) ?? 0}%)</span>
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          {(ev.stocks_refreshed ?? '—')}/{(ev.stocks_attempted ?? '—')}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {(ev.mf_refreshed ?? '—')}/{(ev.mf_attempted ?? '—')}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${health.cls}`}>
                            <Icon size={11} /> {health.text}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right text-gray-500">
                          {ev.duration_sec ? `${ev.duration_sec.toFixed(1)}s` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-gray-500 py-8 text-center" data-testid="events-empty">No refresh history yet. Click "Refresh Prices" to log the first event.</p>
            )
          )}

          {!loading && view === 'daily' && (
            data?.daily_summary?.length ? (
              <table className="w-full text-sm" data-testid="daily-table">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Day</th>
                    <th className="text-right px-3 py-2 font-semibold">Refreshes</th>
                    <th className="text-right px-3 py-2 font-semibold">First Value</th>
                    <th className="text-right px-3 py-2 font-semibold">Last Value</th>
                    <th className="text-right px-3 py-2 font-semibold">Net Change</th>
                    <th className="text-center px-3 py-2 font-semibold">Source Health</th>
                  </tr>
                </thead>
                <tbody>
                  {data.daily_summary.map(row => {
                    const change = row.total_change || 0;
                    return (
                      <tr key={row.date} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-700 font-medium">{fmtDay(row.date)}</td>
                        <td className="px-3 py-2 text-right">{row.refreshes}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{fmtINR(row.first_value)}</td>
                        <td className="px-3 py-2 text-right font-medium">{fmtINR(row.last_value)}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${change >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                          {change >= 0 ? '+' : ''}{fmtINR(change)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex items-center justify-center gap-1 text-xs">
                            {row.healthy_runs > 0 && <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">{row.healthy_runs}</span>}
                            {row.slow_runs > 0 && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">{row.slow_runs}</span>}
                            {row.degraded_runs > 0 && <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">{row.degraded_runs}</span>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-gray-500 py-8 text-center" data-testid="daily-empty">No daily data yet.</p>
            )
          )}
        </div>

        {data && (
          <div className="pt-2 text-xs text-gray-500 border-t mt-2">
            Showing {data.events?.length || 0} of {data.total || 0} recent refresh events • {data.daily_summary?.length || 0} days summarized
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RefreshHistoryModal;
